/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { orderBy, cloneDeep } from 'lodash';
import ShortNameField from '../Common/shortNameField';
import DetailStatus from '../Common/detailStatus';
import DetailHeader from '../Features/detailHeader';
import DetailsTab from './detailsTab';
import ChildrenTab from './childrenTab';
import Metrics from '../WorkPackage/metrics';
import TargetSelect from '../Common/targetSelect';
import ModalNavigation from '../Common/modalNavigation';
import ShareButton from '../Common/shareButton';
import modalTypes from '../../constants/modalTypes';
import { fetchUsers } from '../../actions/userActions';
import { getAllCadences } from '../../actions/cadenceActions';
import { openModal, updateModal } from '../../actions/modalActions';
import { WorkPackageStatuses } from '../../constants/storyStatusTypes';
import {
  updateWorkPackage,
  willDeleteWorkPackage,
  createWorkPackage,
  restoreWorkPackage,
  getChildrenProgress
} from '../../actions/workPackageActions';
import { allows, handleResultAuthorityChange } from '../../utils/authorization';
import { applicationError } from '../../actions/notificationActions';
import { currentOrganization } from '../../utils/organizationHelpers';
import {
  isProductAssociatedWithSpace,
  isTeamAssociatedWithSpace
} from '../../utils/spaceHelpers';
import {
  getModalProductTeam,
  childOrganizationLevel,
  handleOrgLevelChange,
  defaultNewResultAttributes
} from '../../utils/resultModalHelpers';

class CapabilityDetail extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ...defaultNewResultAttributes(props, 'features'),
      isShortNameValid: true,
      changingOrgLevel: false,
      activeTab: 'details'
    };
  }

  componentDidMount() {
    this.checkIfItemExists();
    const { result, children } = this.props;
    if (result?.id && children && children.length > 0)
      this.props.getChildrenProgress(children);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.result !== this.props.result &&
      !!(this.props.result && this.props.result.id)
    ) {
      this.setState({ result: this.props.result, hasChanged: false });
    }

    this.checkIfItemExists();
  }

  getMetricData() {
    const progress = this.props.childrenProgress;
    let r1Children;
    if (progress !== undefined && Object.keys(progress).length !== 0) {
      const keys = Object.keys(progress);
      const arr = [];
      keys.forEach((e) => {
        const value = progress[e].children_count;
        arr.push(value);
      });
      const reducer = (prev, curr) => prev + curr;
      r1Children = arr.reduce(reducer);
    }
    return r1Children || 0;
  }

  checkIfItemExists = () => {
    if (
      this.props.id &&
      !(this.props.result && this.props.result.id) &&
      !this.state.changingOrgLevel
    ) {
      this.props.onClose();
      this.props.applicationError(
        'Link you tried to visit is no longer valid.'
      );
    }
  };

  changeOrgLevel = (orgLevel) => {
    const { result } = this.props;
    this.setState({ changingOrgLevel: true }, () => {
      handleOrgLevelChange(orgLevel, result, this.closeForm);
      if (result.level === orgLevel.level) {
        this.setState({ changingOrgLevel: false });
      }
    });
  };

  handleNewFieldChange = (e) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);

    let hasChanged = true;
    if (e.short_name) {
      resultClone.short_name = e.short_name;
      this.setState({ isShortNameValid: true });
    } else if (e.description) {
      resultClone.description = e.description;
    } else if (e.owner_id || e.owner_id === null) {
      resultClone.owner_id = e.owner_id;
    } else if (e.product_owner_id || e.product_owner_id === null) {
      resultClone.product_owner_id = e.product_owner_id;
    } else if (e.state) {
      resultClone.state = e.state;
    } else if (e.target && e.target.name) {
      resultClone[e.target.name] = e.target.value;
    } else {
      hasChanged = false;
    }
    this.setState({ result: resultClone, hasChanged });
  };

  createTemporaryStory = (tempStory) => {
    const story = tempStory;
    const { features } = this.state;
    const storyIndex = features.findIndex(
      (s) => s.temporaryId === story.temporaryId
    );
    if (storyIndex > -1) {
      const newStories = features.map((s) => {
        if (s.temporaryId === story.temporaryId) {
          return story;
        }
        return s;
      });
      this.setState({ features: newStories });
    } else {
      story.temporaryId = features.length + 1;
      story.owner_initials = [];
      story.owners = [];
      const newFeatures = [...features];

      newFeatures.push(story);
      this.setState({ features: newFeatures });
    }
  };

  updateTemporaryStory = (temporaryId, attrs) => {
    const { features } = this.state;
    const featuresClone = cloneDeep(features);
    const newFeatures = featuresClone.map((f) =>
      f.temporaryId === temporaryId ? { ...f, ...attrs } : f
    );
    this.setState({ features: newFeatures });
  };


  removeTemporaryStory = (storyIndex) => {
    const { features } = this.state;
    const newFeatures = features.filter((s, index) => index !== storyIndex);
    this.setState({ features: newFeatures });
  };

  createNewWorkPackage = () => {
    const { result, criteria, attachmentList, features } = this.state;
    const { organizationLevels } = this.props;

    if (!result.short_name) {
      this.setState({ isShortNameValid: false });
      return;
    }
    const cadenceId = result.cadence_id;
    const resultType = {
      ...result.result_type,
      organization_level_id: this.props.organizationLevelId
    };
    const { state } = result;

    this.props.createWorkPackage({
      state,
      criteria,
      data: { work_package: { ...result } },
      cadence: cadenceId,
      type: resultType,
      attachments: attachmentList,
      children: cloneDeep(features),
      modalId: 'newCapability',
      childOrgLevel: {
        organization_level_id: childOrganizationLevel(
          result,
          organizationLevels
        ).id
      }
    });
  };

  closeForm = () => {
    const { hasChanged } = this.state;

    if (hasChanged) {
      const message = 'Do you want to save the changes you made in this form?';
      const actions = {
        save: {
          name: 'Save',
          function: this.createNewWorkPackage
        },
        dontSave: {
          name: "Don't save",
          function: this.deleteStorageAndClose
        }
      };
      this.props.openModal(
        modalTypes.message,
        {
          message,
          actions,
          closeButtonTitle: 'Cancel'
        },
        'confirm'
      );
    } else {
      this.props.onClose();
    }
  };

  deleteStorageAndClose = () => {
    sessionStorage.removeItem('result');
    sessionStorage.removeItem('criteria');
    sessionStorage.removeItem('attachmentList');
    sessionStorage.removeItem('features');
    this.props.onClose();
  };

  handleFieldChange = (e) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    const field = e;
    const fieldName = Object.keys(field)[0];
    if (fieldName === 'short_name') {
      if (!field.short_name) {
        resultClone.short_name = field.short_name;
        return this.setState({
          isShortNameValid: false,
          result: resultClone
        });
      }
      if (field.short_name === result.short_name) return null;
    }
    this.props.updateWorkPackage(this.props.result.id, field);
  };

  handleTypeAttributes = (typeAttributes) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    if (result.result_type) {
      resultClone.result_type.type_attributes = typeAttributes;
      this.setState({ result: resultClone });
    }
  };

  handleKeyPress = ({ key, target }) => {
    const { result } = this.state;
    const resultClone = cloneDeep(result);
    if (key === 'Enter' && !result.id) {
      resultClone[target.name] = target.value;
      this.setState({ result: resultClone }, () => this.createNewWorkPackage());
    }
  };

  changeTab = (tabName) => {
    this.setState({ activeTab: tabName });
  };

  addAttachment = (attachment) => {
    const { attachmentList } = this.state;
    const newAttachmentList = [...attachmentList];
    this.setState({ attachmentList: newAttachmentList.concat(attachment) });
  };

  addCriteria = (criterion) => {
    const { criteria } = this.state;
    let newCriteria = [...criteria];
    if (criterion.tempId) {
      newCriteria = newCriteria.map((item) => {
        if (item.tempId === criterion.tempId) {
          return {
            ...item,
            short_name: criterion.short_name
          };
        }
        return item;
      });
    } else newCriteria.push({ ...criterion, tempId: criteria.length + 1 });
    this.setState({ criteria: newCriteria });
  };

  removeCriteria = (criterionIndex) => {
    const { criteria } = this.state;
    const newCriteria = criteria.filter((c, index) => index !== criterionIndex);
    this.setState({ criteria: newCriteria });
  };

  removeAttachment = (attachmentIndex) => {
    const { attachmentList } = this.state;
    const newAttachmentList = attachmentList.filter(
      (att, index) => index !== attachmentIndex
    );
    this.setState({ attachmentList: newAttachmentList });
  };

  showModal = (message, actions) => {
    this.props.openModal(
      modalTypes.message,
      {
        message,
        actions,
        closeButtonTitle: 'Cancel'
      },
      'confirm'
    );
  };

  handleSelectChange = (currValue, name) => {
    const { result } = this.state;
    const { updateWorkPackage, onClose } = this.props;
    if (!result.id) {
      let resultClone;
      if (name === 'product_id' || name === 'team_id') {
        resultClone = handleResultAuthorityChange(result, name, currValue);
      } else {
        resultClone = { ...result, [name]: currValue };
      }
      this.setState({ result: resultClone, hasChanged: true });
      return;
    }

    const field = { work_package: { [name]: currValue } };

    const message = `The ${
      name.split('_')[0]
    } you have selected is not part of the active space; this item will not be visible. Continue?`;

    const actions = {
      continue: {
        name: 'Continue',
        function: () => {
          updateWorkPackage(result.id, field);
          onClose();
        }
      }
    };

    if (name === 'product_id') {
      if (isProductAssociatedWithSpace(currValue))
        updateWorkPackage(result.id, field);
      else this.showModal(message, actions);
    } else if (name === 'team_id') {
      if (isTeamAssociatedWithSpace(currValue))
        updateWorkPackage(result.id, field);
      else this.showModal(message, actions);
    } else updateWorkPackage(result.id, field);
  };

  renderMetricsTab() {
    // disable metrics tab until r1 desecendents exist
    const r1_children = this.getMetricData() || 0;
    // console.log(' r1 children => ', r1_children);
    switch (r1_children) {
      // if there are points, disable = false
      case undefined:
        return true;
      case 0:
        return true;
      default:
        return false;
    }
  }

  render() {
    // console.log('metrics tab disabled state => ', this.renderMetricsTab());
    const metricsDisabled = this.renderMetricsTab();

    if (!this.props.isOpen) {
      return null;
    }
    const {
      isLoading,
      fromChild,
      children: resultChildren,
      teams,
      products,
      selectedSpace,
      organizationLevels,
      parents,
      metrics,
      featuresList
    } = this.props;
    const {
      features: newFeatures,
      result,
      attachmentList,
      isShortNameValid,
      criteria
    } = this.state;

    const children = result.id > 0 ? resultChildren : newFeatures;

    const parent = parents.find((p) => p.id === result.parent_id) || {};

    const targetId = result.target_id;
    const allowEdit = allows('update', result);

    const featureOrganizationLevel = childOrganizationLevel(
      result,
      organizationLevels
    );

    const stories = result.id
      ? featuresList.filter((feature) => feature.parent_id === feature.id)
      : newFeatures;

    const { product, team } = getModalProductTeam(
      result,
      selectedSpace,
      products,
      teams
    );

    const tabs = result.id
      ? ['details', 'children', 'metrics']
      : ['details', 'children'];

    return (
      <div
        id={`workpackage-detail-${result.id}`}
        className="detail-modal workpackage-detail"
        tabIndex="-1"
        role="dialog"
      >
        <div className="detail-dialog" role="document">
          {isLoading ? <div id="loader" /> : null}
          <div className="detail-content">
            <div className="modal-header detail-header">
              <DetailHeader
                title=""
                type="capability"
                item={result}
                stories={[]}
                options={this.props.options}
                onClose={this.closeForm}
                handleUpdate={this.handleFieldChange}
                handleRemove={this.props.removeWorkPackageFromCadence}
                handleDelete={this.props.willDeleteWorkPackage}
                handleTypeAttributes={this.handleTypeAttributes}
                handleRestore={this.props.restoreWorkPackage}
                fromChild={fromChild}
                changeOrgLevel={this.changeOrgLevel}
                selectedSpace={selectedSpace}
              />
            </div>
            <div
              className={`modal-body ${this.state.activeTab} ${
                result.id ? '' : 'is-new'
              }`}
            >
              <div className="short-name-box">
                <ShortNameField
                  autoFocus
                  shortName={result.short_name}
                  onNameChange={
                    result.id
                      ? this.handleFieldChange
                      : this.handleNewFieldChange
                  }
                  allowEdit={allowEdit}
                  onKeyUp={this.handleKeyPress}
                  isValid={isShortNameValid}
                />
                {result.id && <ShareButton />}
              </div>
              <ModalNavigation
                tabs={tabs}
                activeTab={this.state.activeTab || 'details'}
                changeTab={this.changeTab}
                metricsDisabled={metricsDisabled}
              >
                <DetailsTab
                  capability={result}
                  products={this.props.products}
                  user={this.props.user}
                  handleUpdate={
                    result.id
                      ? this.handleFieldChange
                      : this.handleNewFieldChange
                  }
                  parents={parents}
                  parent={parent}
                  onSelectChange={this.handleSelectChange}
                  attachmentList={attachmentList}
                  addAttachment={this.addAttachment}
                  removeAttachment={this.removeAttachment}
                  criteria={criteria}
                  addCriteria={this.addCriteria}
                  removeCriteria={this.removeCriteria}
                  selectedSpace={this.props.selectedSpace}
                  team={team}
                  product={product}
                  teams={teams}
                />
                <ChildrenTab
                  id={result.id}
                  capability={result}
                  capabilityChildren={children}
                  disabled={!allowEdit}
                  createTemporaryStory={this.createTemporaryStory}
                  updateTemporaryStory={this.updateTemporaryStory}
                  featureOrganizationLevel={featureOrganizationLevel}
                />
                {result.id && (
                  <Metrics
                    metrics={metrics[result.id]}
                    workPackage={result}
                    stories={stories}
                    activeTab={this.state.activeTab}
                  />
                )}
              </ModalNavigation>
            </div>
            <div className="modal-footer">
              <div className="row footer-selects">
                {!result.id && (
                  <div className="col-md-3 save-cancel-container">
                    <div className="save-cancel-buttons">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-md button-lavender"
                        onClick={this.createNewWorkPackage}
                        disabled={isLoading}
                      >
                        <strong>save</strong>
                      </button>
                      <button
                        type="button"
                        className="btn btn-md btn-default button-normal"
                        onClick={this.closeForm}
                        disabled={isLoading}
                      >
                        <strong>Cancel</strong>
                      </button>
                    </div>
                  </div>
                )}
                {/*
                <div className="col-md-3">
                  <label htmlFor="increment">PI</label>
                  <select
                    className="form-control"
                    value="PI 2 - COMMERCE"
                    disabled
                  >
                    <option value="PI 2 - COMMERCE">PI 2 - COMMERCE</option>
                  </select>
                </div>
                */}
                <div className="col-md-3">
                  {this.state.activeTab === 'details' && (
                    <TargetSelect
                      label="Target"
                      selected={targetId || -1}
                      disabled={!allowEdit}
                      onSelect={this.handleSelectChange}
                      workPackage={result}
                      fullWidth
                    />
                  )}
                </div>
              </div>
              <DetailStatus
                resultId={result.id}
                status={result.state || WorkPackageStatuses.NOT_READY}
                allowEdit={allowEdit}
                isNew={!result.id}
                handleNewFieldChange={this.handleNewFieldChange}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

CapabilityDetail.propTypes = {
  id: PropTypes.number,
  feature: PropTypes.object,
  options: PropTypes.object,
  isOpen: PropTypes.bool,
  fromChild: PropTypes.bool,
  onClose: PropTypes.func,
  fetchUsers: PropTypes.func,
  getAllCadences: PropTypes.func,
  willDeleteWorkPackage: PropTypes.func,
  updateWorkPackage: PropTypes.func,
  removeWorkPackageFromCadence: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
  const result =
    state.workPackageReducer.capabilities.find((f) => f.id === ownProps.id) ||
    {};

  // this should be in a selector function
  const children = result
    ? orderBy(
        state.workPackageReducer.all.filter(
          (child) => child.parent_id === ownProps.id
        ),
        ['level', 'id']
      )
    : null;

  const { selectedSpace } = state.workPackageReducer;

  const organizationLevels = currentOrganization().organization_levels;

  return {
    result,
    children,
    selectedSpace,
    organizationLevels,
    parents: state.workPackageReducer.epics,
    featuresList: state.workPackageReducer.features,
    options: state.defaultOptionsReducer.defaultOptions,
    user: state.userReducer.user,
    isLoading: state.workPackageReducer.isSending,
    currentProducts: state.defaultOptionsReducer.currentProducts,
    metrics: state.workPackageReducer.metrics,
    teams: state.teamReducer.teams,
    products: state.productReducer.products,
    planwallState: state.savedViewReducer.planwallStates.find(
      (pws) => pws.level === 3
    ),
    kanbanWallState: state.savedViewReducer.kanbanWallState,
    targets: state.targetReducer.targets,
    currentTeams: state.defaultOptionsReducer.currentTeams,
    childrenProgress: state.workPackageReducer.metrics.childrenProgress
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateWorkPackage,
      willDeleteWorkPackage,
      createWorkPackage,
      getAllCadences,
      fetchUsers,
      openModal,
      updateModal,
      applicationError,
      restoreWorkPackage,
      getChildrenProgress
    },
    dispatch
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CapabilityDetail)
);
